import { useFlags } from "@flagsmith/flagsmith/react";
import { useEffect } from "react";
import { useParams } from "react-router-dom";

import { OverviewContainer } from "@components/OverviewContainer/OverviewContainer";
import type { PerimeterSection } from "@components/PerimeterLayout/PerimeterLayout.types";
import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { useReferences } from "@contexts/References/References.context";
import { useSelectedPerimeters } from "@contexts/SelectedPerimeters/SelectedPerimeters.context";
import { useTenant } from "@contexts/Tenant/Tenant.context";
import type { Flags } from "@core/flagsmith/flags.types";
import { usePerimeterConsoAction } from "@hooks/usePerimeterConsoAction";
import type { UrlParam } from "@router/router.types";
import { useGetCustomer } from "@services/apiEcms/useGetCustomer";
import { useGetGroup } from "@services/apiEcms/useGetGroup";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./PerimeterContracts.module.css";
import { PerimeterContractsTable } from "./PerimeterContractsTable/PerimeterContractsTable";

const getVatTaxableStatus = (isVatTaxable: boolean | null) => {
  if (isVatTaxable === null) {
    return "unknown";
  }
  return isVatTaxable ? "yes" : "no";
};

export const PerimeterContracts = () => {
  const { t } = useCustomTranslation("main");
  const { "show-e-invoicing": showEInvoicing } = useFlags<Flags>([
    "show-e-invoicing",
  ]);
  const { currentGroup } = useGroupReferences();
  const { customerId = "" } = useParams<UrlParam>();
  const { references, setReferencesFunc } = useReferences();
  const { setSelectedPerimeters } = useSelectedPerimeters();
  const { hasTariffShield } = useTenant();

  const { isLoading } = useGetGroup({
    groupId: currentGroup,
  });

  const { data: customer } = useGetCustomer(customerId);

  useEffect(() => {
    if (customer) {
      setReferencesFunc({
        customer: {
          id: customer?.customerId,
          name: customer?.companyName,
        },
      });
    }
    return () => {
      setReferencesFunc();
      setSelectedPerimeters([]);
    };
    // TODO fix and test
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const { gas, elec, energiesLoading } = usePerimeterConsoAction({
    isSearch: false,
    customerId,
  });

  const shieldData =
    customer && customerId && hasTariffShield
      ? {
          siren: customer?.siren.substring(0, 9),
          companyName: customer?.companyName,
          customerId,
        }
      : undefined;

  const contactData: PerimeterSection = {
    title: t("perimeter.overview.contract.contact"),
    data: [
      {
        name: t("perimeter.overview.contact.name"),
        value: customer?.contactName,
      },
      {
        name: t("perimeter.overview.contact.email"),
        value: customer?.contactEmail,
      },
    ].filter((item) => Boolean(item.value)),
  };

  const eInvoicingData =
    showEInvoicing.enabled && customer
      ? [
          {
            name: t("perimeter.overview.site.eInvoicing.isVatTaxable"),
            value: t(
              `perimeter.overview.site.eInvoicing.vatTaxable.${getVatTaxableStatus(customer.isVatTaxable)}`,
            ),
          },
        ]
      : [];

  const levelData: PerimeterSection = {
    title: t("perimeter.overview.contract.title"),
    data: [
      {
        name: t("perimeter.customers.customerReference"),
        value: customer?.customerId,
      },
      {
        name: t("perimeter.overview.site.siren"),
        value: customer?.siren,
      },
      {
        name: t("perimeter.overview.site.apeCode"),
        value: customer?.apeCode,
      },
      ...eInvoicingData,
    ].filter((item) => Boolean(item.value)),
  };

  return (
    <div className={classes.container}>
      <OverviewContainer
        areEnergiesLoading={energiesLoading}
        contactData={contactData.data.length > 0 ? contactData : undefined}
        elec={elec}
        gas={gas}
        isGroupLoading={isLoading}
        levelData={levelData.data.length > 0 ? levelData : undefined}
        perimeterNavLevelName={references.customer?.name}
        shieldData={shieldData}
      />
      <PerimeterContractsTable />
    </div>
  );
};
